import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { ExcelImportModal } from '../excelImport';
import { PurchaseHeaderView } from './PurchaseHeaderView';
import { usePurchaseHeader } from './usePurchaseHeader';

/**
 * Page-level header for the Purchases route. Container wires the press
 * handlers from `usePurchaseHeader` into the render-only `View`.
 *
 * Wrapped in an `ErrorBoundary` for the same reason every other
 * Purchases widget is: a render-time failure in the View (or a thrown
 * error from the download path) must not crash the whole page. The
 * boundary's children render the View — never throw from this
 * component itself, since React error boundaries do not catch errors
 * thrown by the component that renders them.
 */
export const PurchaseHeader: React.FC = () => {
    const {
        onAction,
        isGeneratingTemplate,
        isExcelImportOpen,
        onExcelImportOpenChange,
        templateFailed,
        onDismissTemplateError,
    } = usePurchaseHeader();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('purchases.actions.errorTitle'),
            errorMessage: t('purchases.actions.errorMessage'),
            errorRetry: t('purchases.actions.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="purchase-header-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="purchase-header-error-boundary"
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <PurchaseHeaderView
                isGeneratingTemplate={isGeneratingTemplate}
                onAction={onAction}
                onDismissTemplateError={onDismissTemplateError}
                templateFailed={templateFailed}
            />
            <ExcelImportModal
                isOpen={isExcelImportOpen}
                onOpenChange={onExcelImportOpenChange}
            />
        </ErrorBoundary>
    );
};
